// src/components/CategoryFilter.tsx
// Merged version: uses api.shop (our separation) + version-based invalidation (our caching work)
// + clean category loading from main branch
import { useEffect, useRef, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAllCategories, getPublicVersions } from '../services/api.shop';
import { CategoryDto } from '../types';
import { useTranslation } from '../context/TranslationContext';
import { categorySlugFromName, translateCategoryName } from '../utils/categoryLabel';

interface CategoryFilterProps {
    selectedCategory: string;
    onCategoryChange: (category: string) => void;
    /** Optional leading control (e.g. mobile Filter chip) — same row as categories */
    leadingSlot?: ReactNode;
}

export default function CategoryFilter({
    selectedCategory,
    onCategoryChange,
    leadingSlot,
}: CategoryFilterProps) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const { data: versions } = useQuery<{ products?: number; categories?: number }>({
        queryKey: ['publicVersions'],
        queryFn: getPublicVersions,
        staleTime: 30 * 1000,
        refetchInterval: 60 * 1000,
        refetchOnWindowFocus: true,
    });

    const prevCategoriesVersion = useRef<number | undefined>(undefined);

    useEffect(() => {
        if (
            versions?.categories &&
            prevCategoriesVersion.current !== undefined &&
            prevCategoriesVersion.current !== versions.categories
        ) {
            queryClient.invalidateQueries({ queryKey: ['categories'] });
        }
        prevCategoriesVersion.current = versions?.categories;
    }, [versions?.categories, queryClient]);

    const { data: apiCategories = [] } = useQuery<CategoryDto[]>({
        queryKey: ['categories', { version: versions?.categories }],
        queryFn: getAllCategories,
        staleTime: 10 * 60 * 1000,
        refetchOnWindowFocus: true,
    });

    const categories = [
        { id: 'all', name: 'All Items', slug: 'all', icon: '🌿' },
        ...apiCategories.map((cat) => {
            const slug = categorySlugFromName(cat.name);
            return {
                id: slug,
                name: cat.name,
                slug,
                icon: cat.icon,
            };
        }),
    ];

    return (
        <div
            className="sticky z-40 border-b border-[var(--accent-color)] bg-[var(--bg-color)]"
            style={{ top: 'var(--shop-header-height, 4rem)' }}
        >
            <div className="px-4 sm:px-6 lg:px-8">
                <div className="scroll-x-fade flex-nowrap gap-2 overflow-x-auto py-3 md:flex-wrap md:overflow-visible">
                    {leadingSlot}
                    {categories.map((category) => (
                        <button
                            key={category.id}
                            type="button"
                            onClick={() => onCategoryChange(category.id)}
                            className={`flex min-h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-sm transition-all ${
                                selectedCategory === category.id
                                    ? 'border-[var(--accent-color)] bg-[var(--accent-color)] text-white shadow-sm'
                                    : 'border-[var(--accent-color)]/30 bg-white/60 text-[var(--text-color)] hover:bg-[var(--bg-color)]'
                            }`}
                            aria-label={`Filter by ${translateCategoryName(t, category.name)}`}
                        >
                            {category.icon ? <span className="text-lg">{category.icon}</span> : null}
                            <span className="text-sm">{translateCategoryName(t, category.name)}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
